// The V-System spread explorer: every way to open a 4-note chord across two
// octaves, the classic drops named, and — the guitar-player's question — which
// spreads a hand can actually finger. Ted Greene catalogued these families as
// V-1…V-14 (close is V-1 and drop-2 is V-2). This is the scouting map behind
// the Workbench's family-aware voicing engine.

import { useMemo, useState } from 'react';
import { chordSymbol, chordTonePitchClasses, type ChordTypeId } from '@/music/chords';
import { enumerateSpreads, findGuitarFingering, type Spread } from '@/music/spreads';
import { getChordPlayer } from '@/audio/player';
import { ChordTypeSelect, NoteSelect } from '@/components/pickers';

const STRIP_H = 120;
const STRIP_W = 46;

function bassFor(pcs: number[]): number {
  // Put the bass note in the cello register (E2..D#3) so audio + frets are real.
  return 40 + ((pcs[0] - 4 + 12) % 12);
}

function SpreadCard({ spread, pcs }: { spread: Spread; pcs: number[] }) {
  const bass = bassFor(pcs);
  const frets = useMemo(() => findGuitarFingering(bass, spread.pitches), [bass, spread]);
  const fretted = frets?.filter((f): f is number => f !== null && f > 0) ?? [];
  const position = frets ? (fretted.length ? `fret ${Math.min(...fretted)}` : 'open') : null;
  const y = (p: number) => STRIP_H - 12 - (p / 24) * (STRIP_H - 24);

  return (
    <div className={`spread-card${spread.name ? ' named' : ''}`} data-testid="spread-card">
      <svg
        width={STRIP_W}
        height={STRIP_H}
        viewBox={`0 0 ${STRIP_W} ${STRIP_H}`}
        role="img"
        aria-label={spread.name ?? `spread ${spread.gaps.join('-')}`}
        onClick={() =>
          void getChordPlayer().playMidi(
            spread.pitches.map((p) => bass + p),
            0.035,
          )
        }
        style={{ cursor: 'pointer' }}
      >
        <line
          x1={STRIP_W / 2}
          y1={y(0)}
          x2={STRIP_W / 2}
          y2={y(spread.span)}
          stroke="var(--hairline)"
        />
        {[0, 12, 24].map((oct) => (
          <line
            key={oct}
            x1={8}
            y1={y(oct)}
            x2={STRIP_W - 8}
            y2={y(oct)}
            stroke="var(--hairline)"
            strokeDasharray="2 3"
          />
        ))}
        {spread.pitches.map((p, i) => (
          <circle
            key={i}
            cx={STRIP_W / 2}
            cy={y(p)}
            r={5}
            fill={i === 0 ? 'var(--ink)' : 'var(--accent)'}
          />
        ))}
      </svg>
      <div className="spread-meta">
        <div className="spread-name">{spread.name ?? `${spread.gaps.join('·')}`}</div>
        <div className="muted">span {spread.span}</div>
        <div className={position ? 'spread-ok' : 'spread-no'}>{position ?? 'beyond the hand'}</div>
      </div>
    </div>
  );
}

export function VSystemExhibit() {
  const [root, setRoot] = useState('C');
  const [typeId, setTypeId] = useState<ChordTypeId>('maj7');

  const pcs = useMemo(() => chordTonePitchClasses(root, typeId), [root, typeId]);
  const spreads = useMemo(() => enumerateSpreads(pcs), [pcs]);
  const playable = spreads.filter((s) => findGuitarFingering(bassFor(pcs), s.pitches)).length;

  return (
    <div className="lab-exhibit" data-testid="lab-vsystem">
      <p className="lab-lede">
        A <em>voicing type</em> places the same four notes in different octaves (Tymoczko&rsquo;s
        octave-displacement class). Ted Greene listed the guitar&rsquo;s types as{' '}
        <strong>V-1 to V-14</strong>: close position is V-1 and <strong>drop 2 is V-2</strong>. The
        workbench voices drop 2 and drop 3. Below is every spread of the chord within two octaves,
        with the standard drops named, each checked for a fingering on four strings within five
        frets. Click a strip to hear it.
      </p>

      <div className="lab-controls">
        <NoteSelect value={root} onChange={setRoot} aria-label="Spread root" />
        <ChordTypeSelect value={typeId} onChange={setTypeId} aria-label="Spread chord type" />
        <span className="muted" style={{ alignSelf: 'center' }} data-testid="vsystem-count">
          {chordSymbol(root, typeId)}: {spreads.length} spreads, {playable} playable
        </span>
      </div>

      <div className="lab-cards" data-testid="spread-cards">
        {spreads.map((s) => (
          <SpreadCard key={s.gaps.join(',')} spread={s} pcs={pcs} />
        ))}
      </div>

      <p className="muted">
        Most playable spreads are the named drops. The wide unnamed spreads are Greene&rsquo;s V-6
        and above, mostly played on piano. <strong>Drop 2 and 4</strong> is the next type to add to
        the workbench.
      </p>
    </div>
  );
}
